/* eslint-disable prefer-promise-reject-errors */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { endpoints } from 'services';
import Paths from 'commons/paths';
import { SERVER_URL } from './endpoints';
import { setCicUpdate } from 'utils/LGLifeUtils';

interface RequestProps {
  multiPart?: boolean;
  skipGatewayRedirect?: boolean;
  noAuth?: boolean;
  verifyRefresh?: string;
}

async function call(axiosConfig: AxiosRequestConfig, requestProp?: RequestProps | undefined) {
  if (!requestProp?.noAuth) {
    let authorization = 'Bearer ';

    if (requestProp?.verifyRefresh) {
      authorization += requestProp.verifyRefresh;
    } else {
      authorization += localStorage.getItem('llAccessToken');
    }

    axios.defaults.headers.common.Authorization = authorization;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }

  if (requestProp?.multiPart) {
    axios.defaults.headers['Content-Type'] = 'multipart/form-data';
  } else {
    axios.defaults.headers['Content-Type'] = 'application/json';
  }

  try {
    const { data, status }: AxiosResponse = await axios(axiosConfig);
    if (status !== 200) {
      console.error('reject', status, data);
      return Promise.reject({ status, data });
    }

    return Promise.resolve(data);
  } catch (error: any) {
    // 504 Gateway Timeout 에러 처리
    if (error.response && error.response.status === 504) {
      // EP 패널(/ep)에서는 컴포넌트 내부에서 처리하므로 리다이렉트 생략
      if (!requestProp?.skipGatewayRedirect && !window.location.pathname.startsWith('/ep')) {
        window.location.href = Paths.getPath('504');
      }
      return Promise.reject({ status: 504, error: '504 Gateway Timeout' });
    }

    return Promise.reject({ error: error || 'Something bad happened' });
  }
}

export async function refresh(): Promise<any> {
  axios.defaults.headers['Content-Type'] = 'application/json';
  axios.defaults.headers.common['Authorization'] = undefined;

  // accessToken 갱신시도
  await axios({
    baseURL: SERVER_URL,
    method: 'POST',
    url: endpoints.auth.tokenRefresh,
    data: {
      refreshToken: localStorage.getItem('llRefreshToken'),
    },
  }).then(response => {
    if (response.data.resultCode === 200 && response.data.accessToken) {
      // accessToken 갱신성공
      setCicUpdate(response.data.shouldSyncCic);

      localStorage.setItem('llAccessToken', response.data.accessToken);

      axios.defaults.headers.common.Authorization = `Bearer ${response.data.accessToken}`;

      return Promise.resolve();
    } else if (response.data.resultCode === 1002) {
      // refresh Token 만료되어 로그인 페이지로 보냄
      localStorage.removeItem('llAccessToken');
      localStorage.removeItem('llRefreshToken');

      window.location.href = Paths.getPath('login');
      // alert('토큰이 만료되어 로그인 페이지로 이동합니다.');
      return Promise.reject();
    }

    console.error('error case');
    return Promise.reject();
  });
}

async function request(axiosConfig: AxiosRequestConfig, requestProp?: RequestProps | undefined): Promise<any> {
  axiosConfig.baseURL = SERVER_URL;
  if (!axiosConfig.url) {
    return Promise.reject();
  }

  axios.defaults.headers['Cache-Control'] = 'no-cache';
  axios.defaults.headers['Pragma'] = 'no-cache';
  axios.defaults.headers['Expires'] = '0';

  // 1. API 호출 (공통 로직)
  return call(axiosConfig, requestProp).then(function (data) {
    // 2. 토큰 만료 응답 감지 (resultCode: 1001)
    if (data.resultCode === 1001) {
      // 3. 자동으로 토큰 갱신
      return refresh().then(function () {
        // 4. 원래 요청 다시 실행
        return call(axiosConfig, requestProp);
      });
    }

    return Promise.resolve(data);
  });
}

export default request;
